import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError, shortDate, TX_LABELS } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, User, CaretDown, Pencil, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import MoneyInput from "../components/MoneyInput";

export default function Clients() {
    const { agencyId, user } = useAuth();
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [txByClient, setTxByClient] = useState({});
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        tax_code: "",
        username: "",
        fido_limit: "",
        notes: "",
    });
    const [editOpen, setEditOpen] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        phone: "",
        email: "",
        tax_code: "",
        username: "",
        fido_limit: "",
        notes: "",
    });
    const [confirmDel, setConfirmDel] = useState(null);
    const [sortBy, setSortBy] = useState("name"); // name | debt | credit | fido | created
    const [sortDir, setSortDir] = useState("asc"); // asc | desc
    const canEdit = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!agencyId) return;
        api.get(`/clients`, { params: { agency_id: agencyId } }).then((r) =>
            setClients(r.data),
        );
    };

    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const toggle = async (id) => {
        const next = expanded === id ? null : id;
        setExpanded(next);
        if (next && !txByClient[next]) {
            const r = await api.get(`/clients/${next}/transactions`);
            setTxByClient((m) => ({ ...m, [next]: r.data }));
        }
    };

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/clients", {
                name: form.name,
                phone: form.phone,
                email: form.email,
                tax_code: form.tax_code,
                username: form.username,
                fido_limit: parseFloat(form.fido_limit) || 0,
                notes: form.notes,
                agency_id: agencyId,
            });
            toast.success("Cliente aggiunto");
            setOpen(false);
            setForm({ name: "", phone: "", email: "", tax_code: "", username: "", fido_limit: "", notes: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/clients/${confirmDel.id}`);
            toast.success("Cliente eliminato");
            setConfirmDel(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const openEdit = (c) => {
        setEditForm({
            name: c.name,
            phone: c.phone || "",
            email: c.email || "",
            tax_code: c.tax_code || "",
            username: c.username || "",
            fido_limit: String(c.fido_limit ?? ""),
            notes: c.notes || "",
        });
        setEditOpen(c);
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/clients/${editOpen.id}`, {
                name: editForm.name.trim(),
                phone: editForm.phone.trim(),
                email: editForm.email.trim(),
                tax_code: editForm.tax_code.trim(),
                username: editForm.username.trim(),
                fido_limit: parseFloat(editForm.fido_limit) || 0,
                notes: editForm.notes.trim(),
            });
            toast.success("Cliente aggiornato");
            setEditOpen(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const totalDebt = clients.reduce(
        (s, c) => s + Math.max(0, c.outstanding_debt || 0),
        0,
    );
    const totalCredit = clients.reduce(
        (s, c) => s + Math.max(0, -(c.outstanding_debt || 0)),
        0,
    );

    const sortedClients = React.useMemo(() => {
        const arr = [...clients];
        const dir = sortDir === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            let av, bv;
            if (sortBy === "name") {
                return a.name.localeCompare(b.name, "it", { sensitivity: "base" }) * dir;
            } else if (sortBy === "debt") {
                // Solo importo positivo (debito), credito = 0
                av = Math.max(0, a.outstanding_debt || 0);
                bv = Math.max(0, b.outstanding_debt || 0);
            } else if (sortBy === "credit") {
                av = Math.max(0, -(a.outstanding_debt || 0));
                bv = Math.max(0, -(b.outstanding_debt || 0));
            } else if (sortBy === "fido") {
                av = a.fido_limit || 0;
                bv = b.fido_limit || 0;
            } else if (sortBy === "created") {
                av = a.created_at || "";
                bv = b.created_at || "";
                return av.localeCompare(bv) * dir;
            } else {
                av = 0; bv = 0;
            }
            return (av - bv) * dir;
        });
        return arr;
    }, [clients, sortBy, sortDir]);

    const toggleSort = (key) => {
        if (sortBy === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(key);
            // default: nome asc, valori numerici desc (più alti per primi)
            setSortDir(key === "name" ? "asc" : "desc");
        }
    };

    return (
        <div data-testid="clients-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Sospesi</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Clienti
                    </h1>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-client-btn"
                >
                    <Plus size={14} weight="bold" /> Nuovo cliente
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 grid-room bg-white mb-8">
                <div className="p-6 grid-cell">
                    <div className="overline mb-2">Totale clienti</div>
                    <div className="text-3xl font-black tabular-nums">
                        {clients.length}
                    </div>
                </div>
                {user?.role !== "operator" && (
                    <>
                        <div className="p-6 grid-cell">
                            <div className="overline mb-2">Soldi fuori (debiti)</div>
                            <div
                                className="text-3xl font-black tabular-nums"
                                style={{ color: totalDebt > 0 ? "#FF2A00" : "#09090b" }}
                                data-testid="clients-total-debt"
                            >
                                {euro(totalDebt)}
                            </div>
                        </div>
                        <div className="p-6 grid-cell">
                            <div className="overline mb-2">Crediti clienti</div>
                            <div
                                className="text-3xl font-black tabular-nums"
                                style={{ color: totalCredit > 0 ? "#00C853" : "#09090b" }}
                                data-testid="clients-total-credit"
                            >
                                {euro(totalCredit)}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                                Soldi che l&apos;agenzia deve ai clienti
                            </div>
                        </div>
                        <div className="p-6 grid-cell">
                            <div className="overline mb-2">Saldo netto</div>
                            <div
                                className="text-3xl font-black tabular-nums"
                                style={{ color: totalDebt - totalCredit >= 0 ? "#FF2A00" : "#00C853" }}
                            >
                                {euro(totalDebt - totalCredit)}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="bg-white border border-zinc-200">
                {clients.length > 0 && (
                    <SortBar
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onToggle={toggleSort}
                        count={clients.length}
                    />
                )}
                {clients.length === 0 && (
                    <div className="p-12 text-center text-zinc-500">
                        Nessun cliente ancora.
                    </div>
                )}
                {sortedClients.map((c) => {
                    const debt = c.outstanding_debt || 0;
                    const isCredit = debt < -0.01;
                    const isDebt = debt > 0.01;
                    const pct =
                        c.fido_limit > 0 && isDebt
                            ? Math.min(100, (debt / c.fido_limit) * 100)
                            : 0;
                    const over = isDebt && debt > c.fido_limit && c.fido_limit > 0;
                    const balanceLabel = isCredit
                        ? "Credito"
                        : isDebt
                          ? "Debito"
                          : "Saldo";
                    const balanceColor = isCredit
                        ? "#00C853"
                        : over
                          ? "#FF2A00"
                          : "#09090b";
                    return (
                        <div
                            key={c.id}
                            className="border-b border-zinc-100 last:border-b-0"
                            data-testid={`client-row-${c.id}`}
                        >
                            <div
                                onClick={() => toggle(c.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") toggle(c.id);
                                }}
                                className="w-full text-left p-4 hover:bg-zinc-50 transition-colors grid grid-cols-12 gap-4 items-center cursor-pointer"
                            >
                                <div className="col-span-12 md:col-span-3 flex items-center gap-3">
                                    <div className="w-8 h-8 border border-zinc-300 flex items-center justify-center">
                                        <User size={14} />
                                    </div>
                                    <div>
                                        <div className="font-bold">{c.name}</div>
                                        <div className="text-xs text-zinc-500 truncate">
                                            {[c.phone, c.email].filter(Boolean).join(" · ") || "—"}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-2">
                                    <div className="overline">{balanceLabel}</div>
                                    <div
                                        className="tabular-nums font-bold"
                                        style={{ color: balanceColor }}
                                        data-testid={`client-balance-${c.id}`}
                                    >
                                        {isCredit ? `−${euro(-debt)}` : euro(debt)}
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-2">
                                    <div className="overline">Fido</div>
                                    <div className="tabular-nums font-bold">
                                        {euro(c.fido_limit)}
                                    </div>
                                </div>
                                <div className="col-span-12 md:col-span-4">
                                    {isCredit ? (
                                        <div className="text-xs text-green-700 font-bold uppercase tracking-wider">
                                            Cliente a credito
                                        </div>
                                    ) : (
                                        <>
                                            <div className="h-2 bg-zinc-100 w-full">
                                                <div
                                                    className="h-full transition-all"
                                                    style={{
                                                        width: `${pct}%`,
                                                        background: over
                                                            ? "#FF2A00"
                                                            : pct > 80
                                                              ? "#FFB300"
                                                              : "#002FA7",
                                                    }}
                                                />
                                            </div>
                                            <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                                                {pct.toFixed(0)}% {over && "· OLTRE FIDO"}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="col-span-12 md:col-span-1 flex justify-end gap-2">
                                    {canEdit && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openEdit(c);
                                                }}
                                                className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-ink hover:bg-zinc-100 transition-colors"
                                                data-testid={`edit-client-${c.id}`}
                                                aria-label="Modifica cliente"
                                            >
                                                <Pencil size={14} weight="bold" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setConfirmDel(c);
                                                }}
                                                className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 transition-colors"
                                                data-testid={`delete-client-${c.id}`}
                                                aria-label="Elimina cliente"
                                            >
                                                <Trash size={14} weight="bold" />
                                            </button>
                                        </>
                                    )}
                                    <CaretDown
                                        size={14}
                                        weight="bold"
                                        style={{
                                            transform:
                                                expanded === c.id ? "rotate(180deg)" : "rotate(0)",
                                            transition: "transform 150ms ease",
                                        }}
                                    />
                                </div>
                            </div>
                            {expanded === c.id && (
                                <div className="bg-zinc-50 p-4 border-t border-zinc-100">
                                    {(c.email || c.tax_code || c.username || c.phone || c.notes) && (
                                        <div className="mb-5">
                                            <div className="overline mb-2">Anagrafica</div>
                                            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                                                {c.phone && (
                                                    <div data-testid={`client-detail-phone-${c.id}`}>
                                                        <dt className="text-zinc-500 uppercase tracking-wider">Telefono</dt>
                                                        <dd className="font-bold mt-0.5 break-all">{c.phone}</dd>
                                                    </div>
                                                )}
                                                {c.email && (
                                                    <div data-testid={`client-detail-email-${c.id}`}>
                                                        <dt className="text-zinc-500 uppercase tracking-wider">Email</dt>
                                                        <dd className="font-bold mt-0.5 break-all">{c.email}</dd>
                                                    </div>
                                                )}
                                                {c.tax_code && (
                                                    <div data-testid={`client-detail-cf-${c.id}`}>
                                                        <dt className="text-zinc-500 uppercase tracking-wider">Codice fiscale</dt>
                                                        <dd className="font-bold mt-0.5 mono">{c.tax_code}</dd>
                                                    </div>
                                                )}
                                                {c.username && (
                                                    <div data-testid={`client-detail-username-${c.id}`}>
                                                        <dt className="text-zinc-500 uppercase tracking-wider">Username online</dt>
                                                        <dd className="font-bold mt-0.5 break-all">{c.username}</dd>
                                                    </div>
                                                )}
                                                {c.notes && (
                                                    <div className="col-span-2 md:col-span-4">
                                                        <dt className="text-zinc-500 uppercase tracking-wider">Note</dt>
                                                        <dd className="mt-0.5 text-zinc-700">{c.notes}</dd>
                                                    </div>
                                                )}
                                            </dl>
                                        </div>
                                    )}
                                    <div className="overline mb-3">Movimenti cliente</div>
                                    <table className="ft-table" data-testid={`client-tx-table-${c.id}`}>
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Tipo</th>
                                                <th>Descrizione</th>
                                                <th className="text-right">Importo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(txByClient[c.id] || []).map((t) => (
                                                <tr key={t.id}>
                                                    <td className="mono text-xs">
                                                        {shortDate(t.occurred_at)}
                                                    </td>
                                                    <td className="text-xs font-bold uppercase">
                                                        {TX_LABELS[t.type]}
                                                    </td>
                                                    <td className="text-zinc-600">
                                                        {t.description || "—"}
                                                    </td>
                                                    <td className="text-right tabular-nums font-bold">
                                                        {euro(t.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                            {(txByClient[c.id] || []).length === 0 && (
                                                <tr>
                                                    <td
                                                        colSpan={4}
                                                        className="text-center text-zinc-500 py-4"
                                                    >
                                                        Nessun movimento
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={create}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="client-form"
                    >
                        <div className="overline mb-3">Nuovo Cliente</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            Anagrafica.
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome completo</div>
                            <input
                                className="inp"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="client-form-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Telefono</div>
                            <input
                                className="inp"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                data-testid="client-form-phone"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Email</div>
                            <input
                                type="email"
                                className="inp"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="client-form-email"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Codice fiscale</div>
                            <input
                                className="inp uppercase"
                                value={form.tax_code}
                                onChange={(e) => setForm({ ...form, tax_code: e.target.value })}
                                maxLength={16}
                                data-testid="client-form-tax-code"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Username online</div>
                            <input
                                className="inp"
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value })}
                                placeholder="es. nickname conto gioco"
                                data-testid="client-form-username"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Fido personale €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={form.fido_limit}
                                onChange={(v) =>
                                    setForm({ ...form, fido_limit: v })
                                }
                                placeholder="0,00"
                                data-testid="client-form-fido"
                            />
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Note</div>
                            <input
                                className="inp"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                data-testid="client-form-notes"
                            />
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="client-form-submit"
                            >
                                Crea
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editOpen && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setEditOpen(null)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={saveEdit}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="client-edit-form"
                    >
                        <div className="overline mb-3">Modifica Cliente</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            {editOpen.name}
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome completo</div>
                            <input
                                className="inp"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                required
                                data-testid="client-edit-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Telefono</div>
                            <input
                                className="inp"
                                value={editForm.phone}
                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                data-testid="client-edit-phone"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Email</div>
                            <input
                                type="email"
                                className="inp"
                                value={editForm.email}
                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                data-testid="client-edit-email"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Codice fiscale</div>
                            <input
                                className="inp uppercase"
                                value={editForm.tax_code}
                                onChange={(e) => setEditForm({ ...editForm, tax_code: e.target.value })}
                                maxLength={16}
                                data-testid="client-edit-tax-code"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Username online</div>
                            <input
                                className="inp"
                                value={editForm.username}
                                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                                placeholder="es. nickname conto gioco"
                                data-testid="client-edit-username"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Fido personale €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={editForm.fido_limit}
                                onChange={(v) =>
                                    setEditForm({ ...editForm, fido_limit: v })
                                }
                                placeholder="0,00"
                                data-testid="client-edit-fido"
                            />
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Note</div>
                            <input
                                className="inp"
                                value={editForm.notes}
                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                data-testid="client-edit-notes"
                            />
                        </label>
                        <div className="mb-6 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                            Saldo attuale:{" "}
                            <strong>
                                {editOpen.outstanding_debt < 0
                                    ? `−${euro(-editOpen.outstanding_debt)} (a credito)`
                                    : euro(editOpen.outstanding_debt)}
                            </strong>
                            . La modifica del fido non altera il saldo esistente.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setEditOpen(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="client-edit-submit"
                            >
                                Salva
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {confirmDel && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirmDel(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="confirm-del-client"
                    >
                        <div className="overline mb-3">Eliminare cliente?</div>
                        <h2 className="text-2xl font-black tracking-tighter mb-4">
                            {confirmDel.name}
                        </h2>
                        <p className="text-sm text-zinc-700 mb-6">
                            Fido {euro(confirmDel.fido_limit)} · Saldo attuale{" "}
                            {confirmDel.outstanding_debt < 0
                                ? `−${euro(-confirmDel.outstanding_debt)} (a credito)`
                                : euro(confirmDel.outstanding_debt)}
                        </p>
                        {Math.abs(confirmDel.outstanding_debt) > 0.01 && (
                            <div className="mb-6 p-3 border border-red-600 text-red-600 text-xs">
                                Non puoi eliminare un cliente con saldo aperto.{" "}
                                {confirmDel.outstanding_debt > 0
                                    ? "Salda il debito con un Versamento Cliente."
                                    : "Restituisci il credito con una Restituzione al Cliente."}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmDel(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={remove}
                                disabled={Math.abs(confirmDel.outstanding_debt) > 0.01}
                                className="btn-destructive flex-1"
                                data-testid="confirm-del-client-btn"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const SORT_OPTIONS = [
    { key: "name", label: "Nome" },
    { key: "debt", label: "Debito" },
    { key: "credit", label: "Credito" },
    { key: "fido", label: "Fido" },
    { key: "created", label: "Aggiunto" },
];

function SortBar({ sortBy, sortDir, onToggle, count }) {
    return (
        <div
            className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-zinc-200 bg-zinc-50"
            data-testid="clients-sort-bar"
        >
            <span className="overline mr-2">Ordina per</span>
            {SORT_OPTIONS.map((opt) => {
                const active = sortBy === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onToggle(opt.key)}
                        className={
                            "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase tracking-wider border transition-colors " +
                            (active
                                ? "bg-ink text-white border-ink"
                                : "bg-white border-zinc-300 text-zinc-700 hover:border-ink")
                        }
                        data-testid={`clients-sort-${opt.key}`}
                        aria-pressed={active}
                    >
                        {opt.label}
                        {active && (
                            sortDir === "asc"
                                ? <ArrowUp size={10} weight="bold" />
                                : <ArrowDown size={10} weight="bold" />
                        )}
                    </button>
                );
            })}
            <span className="ml-auto text-xs text-zinc-500 tabular-nums">{count} cliente{count === 1 ? "" : "i"}</span>
        </div>
    );
}
